<template>
  <component :is="tag" :class="titleClasses">
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed, PropType } from "vue";
import { BOOTSTRAP_FONT_WEIGHTS, TYPOGRAPHY_FONT_WEIGHTS, TITLE_LEVELS } from "@/designSystem/Typography/constant";
import type { FontWeightType, LevelType } from "@/designSystem/Typography/constant";

const props = defineProps({
  color: {
    type: String,
    required: false,
  },

  fontWeight: {
    type: String as () => FontWeightType,
    required: false,
    validator: (value: string) => Object.keys(TYPOGRAPHY_FONT_WEIGHTS).includes(value),
  },

  level: {
    type: String as PropType<LevelType>,
    default: "1",
    validator: (value: LevelType) => TITLE_LEVELS.includes(value),
  },

  upperCase: {
    type: Boolean,
    default: false,
  },

  display: {
    type: Boolean,
    default: false,
  },
});

const tag = computed(() => `h${props.level}`);

const titleClasses = computed(() => {
  return [
    props.color ? `text-${props.color}` : "",
    props.fontWeight ? BOOTSTRAP_FONT_WEIGHTS[TYPOGRAPHY_FONT_WEIGHTS[props.fontWeight]] : "",
    props.upperCase ? "text-uppercase" : "",
    props.display && props.level === TITLE_LEVELS[0] ? "display-1" : "",
    props.display && props.level === TITLE_LEVELS[1] ? "display-2" : "",
  ]
    .filter(Boolean)
    .join(" ");
});
</script>

<style lang="scss" scoped>
@import "@/designSystem/style/main.scss";
@import "bootstrap/scss/mixins/breakpoints";
@import "@/designSystem/style/variables/_font.scss";

@each $level, $font-size in $heading-font-sizes {
  h#{$level} {
    font-weight: map-get($heading-font-weights, $level) !important;

    @include media-breakpoint-between(xs, lg) {
      font-size: map-get($responsive-font-sizes, $level) !important;
    }
  }
}

.display-1 {
  font-size: $display-font-size-1 !important;
  @include media-breakpoint-between(xs, lg) {
    font-size: $responsive-display-font-size-1 !important;
  }
}

.display-2 {
  font-size: $display-font-size-2 !important;
  @include media-breakpoint-between(xs, lg) {
    font-size: $responsive-display-font-size-2 !important;
  }
}
</style>
